import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ClipboardCheck } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useOrg } from "../org";
import { Field, Initials, Select, displayName } from "../people/bits";
import { useLogCare } from "./LogCare";
import { dayLabel } from "./text";

export const STATUS_LABEL = { todo: "To Do", in_progress: "In Progress", completed: "Completed" };
const localISO = () => { const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset()); return d.toISOString().slice(0, 10); };

const dueLine = (a) => (a.status === "completed" ? "Completed" : a.overdue ? `Overdue · was due ${dayLabel(a.due_on)}` : `Due ${dayLabel(a.due_on)}`);

export const AssignmentDialog = ({ person, needId, onClose, onSaved }) => {
  const { api } = useOrg();
  const [team, setTeam] = useState([]);
  const [f, setF] = useState({ title: "", assignee_id: "", due_on: localISO(), instructions: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("/caregivers").then(setTeam).catch((e) => toast.error(e.message)); }, [api]);
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const save = () => {
    setBusy(true);
    api(`/people/${person.id}/assignments`, { method: "POST", body: { ...f, care_need_id: needId || null } })
      .then(() => { toast.success("Care assigned"); onSaved(); }).catch((e) => toast.error(e.message)).finally(() => setBusy(false));
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-paper max-w-md" data-testid="assignment-dialog">
        <DialogTitle className="t-name-lg">Assign care for {person.first_name}</DialogTitle>
        <DialogDescription className="t-meta">A specific action for someone on your team. {person.first_name}'s Assigned Care Leader stays the same.</DialogDescription>
        <div className="space-y-4 mt-2">
          <Field label="What needs to happen"><input value={f.title} onChange={set("title")} maxLength={120} placeholder="Visit after surgery" className="field h-12 px-3" data-testid="assignment-title" /></Field>
          <Field label="Who will do it">
            <Select value={f.assignee_id} onChange={set("assignee_id")} testid="assignment-assignee">
              <option value="">Choose someone…</option>
              {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
            </Select>
          </Field>
          <Field label="Due"><input type="date" min={localISO()} value={f.due_on} onChange={set("due_on")} className="field h-12 px-3" data-testid="assignment-due" /></Field>
          <Field label="Instructions (optional)"><textarea value={f.instructions} onChange={set("instructions")} rows={3} maxLength={1000} className="field p-3" data-testid="assignment-instructions" /></Field>
        </div>
        <button disabled={busy || !f.title.trim() || !f.assignee_id || !f.due_on} onClick={save} className="btn btn-primary w-full mt-5" data-testid="assignment-save">Assign</button>
      </DialogContent>
    </Dialog>
  );
};

const StatusButtons = ({ a, onChange }) => {
  const { api } = useOrg();
  const to = (status) => api(`/assignments/${a.id}`, { method: "PATCH", body: { status } })
    .then(() => { toast.success(status === "completed" ? "Assignment completed" : `Marked ${STATUS_LABEL[status]}`); onChange(); }).catch((e) => toast.error(e.message));
  if (a.status === "completed") return <button onClick={() => to("todo")} className="btn btn-quiet btn-sm" data-testid={`assignment-reopen-${a.id}`}>Reopen</button>;
  return (
    <>
      {a.status === "todo" && <button onClick={() => to("in_progress")} className="btn btn-outline btn-sm" data-testid={`assignment-start-${a.id}`}>Start</button>}
      <button onClick={() => to("completed")} className="btn btn-outline btn-sm" data-testid={`assignment-complete-${a.id}`}>Mark complete</button>
    </>
  );
};

const Row = ({ a, onChange, canEdit }) => (
  <li className="py-4" data-testid={`assignment-${a.id}`}>
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        <p className="t-name-sm">{a.title}</p>
        <p className="t-meta">{a.assignee_name || "Someone"} · <span className={a.overdue ? "text-sun font-semibold" : ""}>{dueLine(a)}</span></p>
        {a.instructions && <p className="t-body text-ink-soft mt-1">{a.instructions}</p>}
      </div>
      <span className="status status-neutral shrink-0" data-testid={`assignment-status-${a.id}`}>{STATUS_LABEL[a.status]}</span>
    </div>
    {(canEdit || a.mine) && <div className="flex flex-wrap gap-2 mt-3"><StatusButtons a={a} onChange={onChange} /></div>}
  </li>
);

export const PersonAssignments = ({ p, c, needId }) => {
  const { api } = useOrg();
  const [items, setItems] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = useCallback(() => api(`/people/${p.id}/assignments`).then(setItems).catch(() => setItems([])), [api, p.id]);
  useEffect(() => { load(); }, [load]);
  if (!items || (!items.length && !c.oversee)) return null;
  return (
    <section className="mt-10" data-testid="person-assignments">
      <div className="flex items-center justify-between gap-3">
        <h2 className="t-label">Care Assignments</h2>
        {c.oversee && <button onClick={() => setAdding(true)} className="btn btn-quiet btn-sm" data-testid="assignment-add-btn">Assign care</button>}
      </div>
      {items.length ? <ul className="rule-list border-b border-line">{items.map((a) => <Row key={a.id} a={a} onChange={load} canEdit={c.oversee} />)}</ul>
        : <p className="t-meta mt-2">No assignments yet.</p>}
      {adding && <AssignmentDialog person={p} needId={needId} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }} />}
    </section>
  );
};

export const AssignedToMe = ({ items, onChange }) => {
  const navigate = useNavigate();
  const openLog = useLogCare();
  return (
    <section className="mt-10" data-testid="today-assigned">
      <h2 className="t-heading">Assigned to me</h2>
      <div className="mt-4 border-t border-line">
        {items.map((a) => (
          <div key={a.id} className="py-6 border-b border-line" data-testid={`today-assignment-${a.id}`}>
            <button className="flex gap-4 text-left w-full group" onClick={() => navigate(`/people/${a.person.id}`)} data-testid={`today-assignment-open-${a.id}`}>
              <Initials p={a.person} size={52} />
              <div className="min-w-0 flex-1">
                <p className="t-name group-hover:text-brand transition-colors">{displayName(a.person)}</p>
                <p className="t-body mt-1">{a.title}</p>
                {a.instructions && <p className="t-meta mt-1">{a.instructions}</p>}
                <p className={`status mt-2 ${a.overdue ? "status-overdue" : a.due_today ? "status-attention" : "status-neutral"}`}>{STATUS_LABEL[a.status]} · {dueLine(a)}{a.assigned_by_name ? ` · from ${a.assigned_by_name}` : ""}</p>
              </div>
            </button>
            <div className="flex flex-wrap gap-2 mt-4 sm:pl-[68px]">
              <button onClick={() => openLog(a.person, { careNeedId: a.care_need_id })} className="btn btn-primary btn-sm" data-testid={`today-assignment-log-${a.id}`}>Log care</button>
              <StatusButtons a={a} onChange={onChange} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export const OverviewAssignments = ({ a }) => {
  const navigate = useNavigate();
  return (
    <section data-testid="overview-assignments">
      <h2 className="t-heading flex items-center gap-2"><ClipboardCheck size={22} className="text-calm" />Care Assignments</h2>
      <p className="t-meta mt-1" data-testid="overview-assignments-summary">{a.open} open · {a.in_progress} in progress · <span className={a.overdue ? "text-sun font-semibold" : ""}>{a.overdue} overdue</span> · {a.completed} completed this period</p>
      {a.items.length > 0 && (
        <ul className="rule-list border-y border-line mt-4">
          {a.items.map((x) => (
            <li key={x.id}><button onClick={() => navigate(`/people/${x.person.id}`)} className="w-full text-left py-3 flex items-start justify-between gap-3 group" data-testid={`overview-assignment-${x.id}`}>
              <span className="min-w-0"><span className="t-name-sm block group-hover:text-brand transition-colors">{x.title} · {displayName(x.person)}</span><span className="t-meta">{x.assignee_name || "Someone"} · {STATUS_LABEL[x.status]}</span></span>
              <span className={`text-sm shrink-0 ${x.overdue ? "text-sun font-semibold" : "text-ink-soft"}`}>{x.overdue ? "Overdue" : dayLabel(x.due_on)}</span>
            </button></li>
          ))}
        </ul>
      )}
    </section>
  );
};
